import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeParameters, analyzeParametersFromAPI } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/* ──────────────────────────────────── */
/* Parameters Analysis — full port     */
/* ──────────────────────────────────── */

const COLORS = ['#818CF8', '#F87171', '#34D399', '#FBBF24', '#60A5FA', '#A78BFA', '#FB923C'];

function ParametersAnalysis() {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [portfolio, setPortfolio] = useState('');
    const [targetAcos, setTargetAcos] = useState('30'); // Changed to string to match input type ? // Dual-mode : 'file' or 'api'
    const [dataSource, setDataSource] = useState('file');
    // [ORIGINAL — BUG-6] const [startDate, setStartDate] = useState(() => { var d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
    const [startDate, setStartDate] = useState(() => { var d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); }); 
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [adType, setAdType] = useState('sp'); // Default to SP
    const [statusMsg, setStatusMsg] = useState('');

    const fileRef = useRef(file);
    // targetAcosRef is no longer needed as targetAcos state is used directly
    const hasResult = useRef(false);
    const debounceRef = useRef(null); // [FIX BUG-3] Debounce ref para handleAcosChange
    useEffect(() => { fileRef.current = file; }, [file]);
    
    useEffect(() => {
        const checkPending = localStorage.getItem('parameters-pending');
        if (checkPending === 'true') {
            setLoading(true);
            const profileId = localStorage.getItem('selectedAccountValue');
            if (profileId) {
                // Not ideal, but we can't easily poll from here without job ID. 
                // Normally we should check cached data like getCachedAnalysis
                setLoading(false);
                localStorage.removeItem('parameters-pending');
            }
        }
    }, []);

    const doAnalyze = useCallback(async (pOverride, aOverride) => {
        if (dataSource === 'file') {
            const f = fileRef.current;
            if (!f) {
                setError('Please select a file to analyze.');
                return;
            }
        }
        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            if (!profileId) {
                setError('Please select an Amazon Ads account in the header before using API mode.');
                return;
            }
        }
        if (dataSource === 'api') {
            localStorage.setItem('parameters-pending', 'true');
        }

        const executeAnalysis = async () => {
            setLoading(true);
            setError('');
            setStatusMsg(dataSource === 'api' ? '🔄 Fetching from Amazon API — this may take a few minutes...' : '🔄 Processing file...');
            try {
                const pVal = pOverride !== undefined ? pOverride : (portfolio || undefined);
                const aVal = aOverride !== undefined ? Number(aOverride) : Number(targetAcos);
                let res;
                if (dataSource === 'api') {
                    const currentPid = localStorage.getItem('selectedAccountValue');
                    res = await analyzeParametersFromAPI(currentPid, startDate, endDate, aVal, pVal, adType, (status, attempt) => {
                        setStatusMsg(`🔄 Polling... (${status}, attempt ${attempt})`);
                    });
                } else {
                    res = await analyzeParameters(fileRef.current, aVal, pVal);
                }
                setResult(res.data);
                hasResult.current = true;
                
                if (res.data) {
                    alertify.success('Data loaded and analysis generated successfully!');
                }
            } catch (err) {
                setError(err.response && err.response.data ? err.response.data.message : err.message);
                // [ORIGINAL — BUG-4] setStatusMsg(''); // Estaba solo en catch, no se limpiaba en éxito
            } finally {
                setLoading(false);
                localStorage.removeItem('parameters-pending');
                setStatusMsg(''); // [FIX BUG-4] Mover aquí para limpiar siempre (éxito o error)
            }
        };

        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            const checkData = async () => {
                setStatusMsg('⏳ Checking local database...');
                try {
                    const { checkMissingDaysParameters } = require('../../services/trackingService');
                    const checkRes = await checkMissingDaysParameters(profileId, startDate, endDate, adType);
                    if (checkRes.missingDays > 0) {
                        setStatusMsg(`📡 ${checkRes.missingDays} days not found in database — Amazon API needed`);
                        alertify.confirm(
                            'Amazon API Fetch Required',
                            `We need to fetch ${checkRes.missingDays} missing days from Amazon API for the selected date range. This process runs in the background and may take several minutes depending on the volume of data. Continue?`,
                            function () { executeAnalysis(); },
                            function () { 
                                alertify.error('Analysis canceled.'); 
                                localStorage.removeItem('parameters-pending'); 
                                setStatusMsg('');
                            }
                        ).set('labels', {ok: 'Yes, fetch from API', cancel: 'Cancel'});
                    } else {
                        setStatusMsg('✅ All data found in database — loading locally...');
                        executeAnalysis();
                    }
                } catch (err) {
                    console.error("Check missing days error", err);
                    executeAnalysis();
                }
            };
            checkData();
        } else {
            executeAnalysis();
        }
    }, [portfolio, targetAcos, dataSource, startDate, endDate, adType, setError, setLoading, setResult]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (dataSource === 'file' && !fileRef.current) {
            setError('Please select a file to analyze.');
            return;
        }
        doAnalyze();
    };

    const handlePortfolioChange = (val) => {
        setPortfolio(val);
        if (hasResult.current) { // No need to check fileRef.current here, as doAnalyze handles it
            setTimeout(() => doAnalyze(val, targetAcos), 50);
        }
    };

    const handleAcosChange = (val) => {
        setTargetAcos(val);
        if (hasResult.current) {
            // [ORIGINAL — BUG-3] setTimeout(() => doAnalyze(portfolio, val), 50);
            clearTimeout(debounceRef.current); // [FIX BUG-3] Debounce 600ms para evitar requests por cada keystroke
            debounceRef.current = setTimeout(() => doAnalyze(portfolio, val), 600);
        }
    };

    const handleExportExcel = async () => {
        if (!result) return;
        
        const asins = result.baseMetrics.map(m => m.asin);
        const wb = new ExcelJS.Workbook();
        
        for (const asin of asins) {
            const ws = wb.addWorksheet(asin);
            
            // Adjust column widths
            ws.columns = [
                { width: 25 }, // A
                { width: 15 }, // B
                { width: 15 }, // C
                { width: 18 }, // D
                { width: 15 }, // E
                { width: 20 }, // F
                { width: 15 }, // G
                { width: 15 }, // H
                { width: 15 }  // I
            ];

            const base = result.baseMetrics.find(m => m.asin === asin);
            
            // Base Metrics
            const baseHeader = ws.addRow(["Metric", "Value"]);
            baseHeader.font = { bold: true };
            baseHeader.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } });
            
            ws.addRow(["Target ACoS", base.targetAcos / 100]).getCell(2).numFmt = '0.00%';
            ws.addRow(["AOV", base.aov]).getCell(2).numFmt = '"$"#,##0.00';
            ws.addRow(["Target CPA", base.targetCpa]).getCell(2).numFmt = '"$"#,##0.00';
            ws.addRow(["Clicks per Order (x)", base.totalOrders > 0 ? base.totalClicks / base.totalOrders : 0]).getCell(2).numFmt = '0.0';
            ws.addRow(["Max CPC", base.maxCpc]).getCell(2).numFmt = '"$"#,##0.00';
            ws.addRow([]);
            ws.addRow([]);

            // Match Type
            const mtHeader = ws.addRow(["Match Type", "Clicks", "Orders", "Conversion Rate", "Average CPC", "Max CPC", "Average ACOS", "Max ACOS", "% Clicks"]);
            mtHeader.font = { bold: true };
            mtHeader.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } });

            const mt = (result.matchTypes || []).find(m => m.asin === asin);
            if (mt && mt.breakdown) {
                mt.breakdown.forEach(b => {
                    const row = ws.addRow([
                        b.matchType, b.clicks, b.orders, b.convRate / 100, b.avgCpc, b.maxCpc, b.avgAcos / 100, b.maxAcos / 100, b.pctClicks / 100
                    ]);
                    row.getCell(4).numFmt = '0.00%'; // Conversion Rate
                    row.getCell(5).numFmt = '"$"#,##0.00'; // Average CPC
                    row.getCell(6).numFmt = '"$"#,##0.00'; // Max CPC
                    row.getCell(7).numFmt = '0.00%'; // Average ACOS
                    row.getCell(8).numFmt = '0.00%'; // Max ACOS
                    row.getCell(9).numFmt = '0.00%'; // % Clicks
                });
            }
            ws.addRow([]);
            ws.addRow([]);

            // Placements
            const plHeader = ws.addRow(["Placement", "Clicks", "Orders", "Conversion Rate", "Average CPC", "New Bid (Max CPC)"]);
            plHeader.font = { bold: true };
            plHeader.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } });

            const pl = (result.placements || []).find(p => p.asin === asin);
            if (pl && pl.placementBreakdown) {
                pl.placementBreakdown.forEach(b => {
                    const row = ws.addRow([
                        b.placement, b.clicks, b.orders, b.cvr / 100, b.currentCpc, b.newBid
                    ]);
                    row.getCell(4).numFmt = '0.00%'; // Conversion Rate
                    row.getCell(5).numFmt = '"$"#,##0.00'; // Average CPC
                    row.getCell(6).numFmt = '"$"#,##0.00'; // New Bid (Max CPC)
                });
            }
        }
        
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'asin_analysis_output.xlsx');
    };

    const charts = result ? result.placementCharts : null;

    /* ── Helper to build stacked bar traces ── */
    const buildStackedTraces = (data, yKey) => {
        if (!data || data.length === 0) return [];
        const placements = [...new Set(data.map(r => r.placement))];
        return placements.map((p, i) => {
            const subset = data.filter(r => r.placement === p);
            return {
                x: subset.map(r => r.date),
                y: subset.map(r => r[yKey]),
                type: 'bar',
                name: p,
                marker: { color: COLORS[i % COLORS.length] },
            };
        });
    };

    /* ── Helper to build line traces ── */
    const buildLineTraces = (data, yKey) => {
        if (!data || data.length === 0) return [];
        const placements = [...new Set(data.map(r => r.placement))];
        return placements.map((p, i) => {
            const subset = data.filter(r => r.placement === p);
            return {
                x: subset.map(r => r.date),
                y: subset.map(r => r[yKey]),
                type: 'scatter',
                mode: 'lines+markers',
                name: p,
                line: { color: COLORS[i % COLORS.length], width: 2 },
                marker: { size: 4 },
            };
        });
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Campaign Parameters Analysis</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Upload / API */}
                <div className="card mb-4">
                    <div className="card-body">


                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <div className="col-md-3">
                                        <label className="form-label">Campaign Report (Excel)</label>
                                        <input type="file" className="form-control" accept=".xlsx,.xls,.csv"
                                            onChange={(e) => setFile(e.target.files[0])} required={dataSource === 'file'} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-md-2">
                                            <label className="form-label">Ad Type</label>
                                            <select className="form-select" value={adType}
                                                onChange={(e) => setAdType(e.target.value)}>
                                                <option value="sp">Sponsored Products</option>
                                                <option value="sb">Sponsored Brands</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">Start Date</label>
                                            <input type="date" className="form-control" value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)} required={dataSource === 'api'} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">End Date</label>
                                            <input type="date" className="form-control" value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)} required={dataSource === 'api'} />
                                        </div>
                                    </>
                                )}
                                <div className="col-md-3">
                                    <label className="form-label">Portfolio (optional)</label>
                                    {result && result.portfolios ? (
                                        <select className="form-select" value={portfolio}
                                            onChange={(e) => handlePortfolioChange(e.target.value)}>
                                            <option value="">All portfolios</option>
                                            {result.portfolios.map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    ) : (
                                        <input className="form-control" value={portfolio}
                                            onChange={(e) => setPortfolio(e.target.value)} placeholder="Filter by portfolio..." />
                                    )}
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Target ACOS %</label>
                                    <input type="number" className="form-control" value={targetAcos}
                                        onChange={(e) => handleAcosChange(e.target.value)} min={1} max={100} />
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary w-100"
                                        disabled={loading || (dataSource === 'file' && !file)}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                            {dataSource === 'api' && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Account: <strong>{localStorage.getItem('selectedAccountName') || 'Not selected'}</strong>
                                    </small>
                                </div>
                            )}
                        </form>
                        {/* Status indicator */}
                        {(loading || statusMsg) && (
                            <div className="mt-2 d-flex align-items-center" style={{ minHeight: 24 }}>
                                {loading && <span className="spinner-border spinner-border-sm text-primary me-2" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                                {statusMsg && <small className="text-muted" style={{ fontSize: '0.8rem' }}>{statusMsg}</small>}
                            </div>
                        )}
                    </div>
                </div>

                {/* No ASIN warning */}
                {result && (!result.baseMetrics || result.baseMetrics.length === 0) && (
                    <div className="alert alert-warning mb-4">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>No ASINs detected in campaign names.</strong> The tables and Excel export require campaigns to have an ASIN (e.g., "B012345678") in their name. The charts below show aggregate data for all campaigns.
                    </div>
                )}

                {/* Base metrics */}
                {result && result.baseMetrics && result.baseMetrics.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Base Metrics per ASIN</h5>
                                <button className="btn btn-sm btn-success" onClick={handleExportExcel}>
                                    <i className="bi bi-file-earmark-excel me-1"></i> Download Excel
                                </button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm table-striped">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>ASIN</th><th>Spend</th><th>Sales</th><th>Orders</th>
                                            <th>AOV</th><th>Target CPA</th><th>CVR%</th><th>Max CPC</th>
                                            <th>Current ACOS</th><th>Target ACOS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.baseMetrics.map(m => (
                                            <tr key={m.asin}>
                                                <td className="fw-bold">{m.asin}</td>
                                                <td>${m.totalSpend.toFixed(2)}</td>
                                                <td>${m.totalSales.toFixed(2)}</td>
                                                <td>{m.totalOrders}</td>
                                                <td>${m.aov.toFixed(2)}</td>
                                                <td>${m.targetCpa.toFixed(2)}</td>
                                                <td>{m.cvr.toFixed(2)}%</td>
                                                <td>${m.maxCpc.toFixed(2)}</td>
                                                {/* [ORIGINAL — BUG-5] <td className={m.currentAcos > targetAcos ? 'text-danger fw-bold' : 'text-success'}> */}
                                                <td className={m.currentAcos > Number(targetAcos) ? 'text-danger fw-bold' : 'text-success'}>{/* [FIX BUG-5] Coerción explícita a Number */}
                                                    {m.currentAcos.toFixed(2)}%
                                                </td>
                                                <td>{m.targetAcos}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Match type tables */}
                {result && result.matchTypes && result.matchTypes.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Match Type Analysis</h5>
                            <div className="row">
                                {result.matchTypes.map(mt => (
                                    <div className="col-12 mb-4" key={mt.asin}>
                                        <h6>{mt.asin}</h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Match Type</th><th>Spend</th><th>Sales</th><th>Orders</th>
                                                        <th>Clicks</th><th>CVR%</th><th>Avg CPC</th><th>Max CPC</th>
                                                        <th>Avg ACOS</th><th>Max ACOS</th><th>% Clicks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mt.breakdown.map((b, i) => (
                                                        <tr key={i}>
                                                            <td className="fw-bold">{b.matchType}</td>
                                                            <td>${b.spend.toFixed(2)}</td>
                                                            <td>${b.sales.toFixed(2)}</td>
                                                            <td>{b.orders}</td>
                                                            <td>{b.clicks}</td>
                                                            <td>{b.convRate.toFixed(2)}%</td>
                                                            <td>${b.avgCpc.toFixed(2)}</td>
                                                            <td>${b.maxCpc.toFixed(2)}</td>
                                                            <td>{b.avgAcos.toFixed(2)}%</td>
                                                            <td>{b.maxAcos.toFixed(2)}%</td>
                                                            <td>{b.pctClicks.toFixed(1)}%</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Placement breakdown with New Bid */}
                {result && result.placements && result.placements.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Placement Analysis &amp; Bid Recommendations</h5>
                            {result.placements.map(p => (
                                <div key={p.asin} className="mb-4">
                                    <h6>{p.asin} — AOV: ${p.aov} | Target CPA: ${p.targetCpa}</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Placement</th><th>Spend</th><th>Sales</th><th>Orders</th>
                                                    <th>ACOS%</th><th>CVR%</th><th>Current CPC</th>
                                                    <th className="bg-warning bg-opacity-25">New Bid</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {p.placementBreakdown.map((pb, i) => (
                                                    <tr key={i}>
                                                        <td>{pb.placement}</td>
                                                        <td>${pb.spend.toFixed(2)}</td>
                                                        <td>${pb.sales.toFixed(2)}</td>
                                                        <td>{pb.orders}</td>
                                                        {/* [ORIGINAL — BUG-5] <td className={pb.acos > targetAcos ? 'text-danger' : 'text-success'}>{pb.acos.toFixed(2)}%</td> */}
                                                        <td className={pb.acos > Number(targetAcos) ? 'text-danger' : 'text-success'}>{pb.acos.toFixed(2)}%</td>{/* [FIX BUG-5] */}
                                                        <td>{pb.cvr.toFixed(2)}%</td>
                                                        <td>${pb.currentCpc.toFixed(2)}</td>
                                                        <td className="fw-bold bg-warning bg-opacity-25">${pb.newBid.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ═══════════ PLACEMENT CHARTS ═══════════ */}
                {charts && (
                    <>
                        {/* 1) Click Distribution by Placement */}
                        {charts.clicksByPlacement && charts.clicksByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            x: charts.clicksByPlacement.map(r => r.placement),
                                            y: charts.clicksByPlacement.map(r => r.clicks),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Clicks: %{y:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Click Distribution by Placement', font: { size: 16 } },
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Total Clicks' } },
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 2) Orders Distribution by Placement */}
                        {charts.ordersByPlacement && charts.ordersByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            x: charts.ordersByPlacement.map(r => r.placement),
                                            y: charts.ordersByPlacement.map(r => r.orders),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Orders: %{y:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Orders Distribution by Placement', font: { size: 16 } },
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Total Orders (7 Days)' } },
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 3) Clicks vs Orders % Distribution */}
                        {charts.clicksVsOrdersPct && charts.clicksVsOrdersPct.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[
                                            {
                                                x: charts.clicksVsOrdersPct.map(r => r.placement),
                                                y: charts.clicksVsOrdersPct.map(r => r.clicksPct),
                                                type: 'bar', name: 'Clicks Percentage',
                                                marker: { color: COLORS[0] },
                                            },
                                            {
                                                x: charts.clicksVsOrdersPct.map(r => r.placement),
                                                y: charts.clicksVsOrdersPct.map(r => r.ordersPct),
                                                type: 'bar', name: 'Orders Percentage',
                                                marker: { color: COLORS[1] },
                                            },
                                        ]}
                                        layout={{
                                            title: { text: 'Clicks vs Orders Percentage Distribution by Placement', font: { size: 16 } },
                                            barmode: 'group',
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Percentage (%)' } },
                                            legend: { title: { text: 'Metric' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 150, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 4) Orders Evolution by Placement Over Time */}
                        {charts.ordersByDate && charts.ordersByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildLineTraces(charts.ordersByDate, 'orders')}
                                        layout={{
                                            title: { text: 'Orders Evolution by Placement Over Time', font: { size: 16 } },
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Total Orders (7 Days)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 5) Evolution of Spend % by Placement (stacked bar) */}
                        {charts.spendPctByDate && charts.spendPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildStackedTraces(charts.spendPctByDate, 'spendPct')}
                                        layout={{
                                            title: { text: 'Evolution of Spend Percentage by Placement Over Time', font: { size: 16 } },
                                            barmode: 'stack',
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Spend Percentage (%)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 6) Evolution of Clicks % by Placement (stacked bar) */}
                        {charts.clicksPctByDate && charts.clicksPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildStackedTraces(charts.clicksPctByDate, 'clicksPct')}
                                        layout={{
                                            title: { text: 'Evolution of Clicks Percentage by Placement Over Time', font: { size: 16 } },
                                            barmode: 'stack',
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Clicks Percentage (%)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 7) Top Campaigns by Clicks in Product Pages */}
                        {charts.topCampaignsByClicks && charts.topCampaignsByClicks.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsByClicks.map(r => r.campaign).reverse(),
                                            x: charts.topCampaignsByClicks.map(r => r.clicks).reverse(),
                                            type: 'bar',
                                            orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsByClicks.map(r => r.clicks).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Clicks: %{x:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Campaigns with Highest Clicks in Product Pages', font: { size: 16 } },
                                            xaxis: { title: { text: 'Total Clicks' } },
                                            yaxis: { title: { text: 'Campaign Name' }, automargin: true },
                                            margin: { l: 250, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsByClicks.length * 28 + 100),
                                            showlegend: false,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 8) Top Campaigns by Spend in Product Pages */}
                        {charts.topCampaignsBySpend && charts.topCampaignsBySpend.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5>Top Spend in Product Pages (&gt; $10)</h5>
                                    <div className="table-responsive mb-3">
                                        <table className="table table-sm table-striped">
                                            <thead className="table-dark">
                                                <tr><th>Campaign Name</th><th>Total Spend</th></tr>
                                            </thead>
                                            <tbody>
                                                {charts.topCampaignsBySpend.map((r, i) => (
                                                    <tr key={i}>
                                                        <td>{r.campaign}</td>
                                                        <td>${r.spend.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsBySpend.map(r => r.campaign).reverse(),
                                            x: charts.topCampaignsBySpend.map(r => r.spend).reverse(),
                                            type: 'bar',
                                            orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsBySpend.map(r => r.spend).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Spend: $%{x:,.2f}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Campaigns with Highest Spend in Product Pages (> $10)', font: { size: 16 } },
                                            xaxis: { title: { text: 'Total Spend' } },
                                            yaxis: { title: { text: 'Campaign Name' }, automargin: true },
                                            margin: { l: 250, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsBySpend.length * 28 + 100),
                                            showlegend: false,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 9) Spend Evolution by Placement Over Time (line) */}
                        {charts.spendByDate && charts.spendByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildLineTraces(charts.spendByDate, 'spend')}
                                        layout={{
                                            title: { text: 'Spend Evolution by Placement Over Time', font: { size: 16 } },
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Total Spend' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default ParametersAnalysis;
